import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
// @ts-ignore
import {CommonModule} from '@angular/common';
// @ts-ignore
import {FormsModule} from '@angular/forms';
// @ts-ignore
import {Component, OnInit} from '@angular/core';
// @ts-ignore
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
// @ts-ignore
import {firstValueFrom} from 'rxjs';
import {ComparacaoEstoqueService, ComparacaoEstoqueItem, ComparacaoEstoqueResposta, TipoComparacao} from '../../services/comparacao-estoque.service';
import {AerosysGatewayService, AerosysEmpresa} from '../../services/aerosys-gateway.service';
import {EmpresaStateService} from '../../services/empresa-state.service';
import {EstoqueService} from '../../services/estoque.service';
import {SankhyaGatewayService} from '../../services/sankhya-gateway.service';

/** Uma linha da prévia da Nota de Correção (ver abrirModalNota/_executarLotesNota). */
interface ItemPreviewNota {
    codigo: string;
    descricao: string;
    qtdeAtual: number | null;
    qtdeNova: number | null;
    localAtual: string;
    localNovo: string;
    codVolume: string;
    /**
     * Fase 2 (seriais) -- preenchido só pra itens serial-controlados
     * (TIPCONTEST='E' em TGFPRO) DEPOIS que o modal abre, via
     * _resolverSeriaisPreview(). Lista de ICS_NS (Aerosys) que serão
     * atribuídos como TGFSER ao gerar a nota -- pode ter MENOS itens que
     * qtdeNova quando o Aerosys não tem seriais disponíveis suficientes
     * (ver avisoSerial nesse caso, "gera o que achar e avisa").
     */
    seriais?: string[];
    /** Aviso de divergência/indisponibilidade de série pra este item (Fase 2). Não bloqueia a geração. */
    avisoSerial?: string;
    /**
     * Fase 3 (lote/form) -- preenchido só pra itens lote-controlados
     * (TIPCONTEST='L' em TGFPRO) via _resolverFormsPreview(). Detalhamento de
     * QUAIS lotes/forms do Aerosys cobrem a quantidade corrigida, em ordem
     * FIFO -- pode ter MAIS de 1 entrada quando um lote sozinho não cobre
     * tudo (decisão explícita do cliente: não pode perder rastreabilidade de
     * nenhum form, então cada um vira sua PRÓPRIA linha na nota -- ver
     * _montarItensSubmissao/ItemSubmissaoNota, já que TGFITE.CONTROLE é um
     * valor único por linha).
     */
    alocacoesLote?: { controle: string; qtde: number }[];
    /** Aviso quando o Aerosys não tinha saldo de form suficiente pra cobrir toda a qtdeNova (Fase 3). Não bloqueia a geração. */
    avisoLote?: string;
    /**
     * Documentos (PDF via ICO_FORM_CAMINHO, Aerosys) encontrados pra este
     * produto -- preenchido via _resolverDocumentosPreview(). SÓ documentação
     * (anexo na nota gerada, não é fonte de dado nenhum) -- ver
     * comentário grande em _anexarDocumentosNaNota. Mais recente primeiro.
     */
    documentos?: { icoId: number; formNumero: string; formLote: string; formData: string | null;
                   caminho: string; formStatus: string; formInvoice: string;
                   cprNumTitulo: string; cprStatus: string; cprDataEmissao: string | null }[];
    /** icoId do documento escolhido pra anexar (null = não anexar nenhum pra este item). Default = o mais recente, se houver. */
    documentoEscolhidoIcoId?: number | null;
}

/**
 * Uma LINHA que será efetivamente enviada ao addon (1 por produto na Fase 1/2,
 * mas PODE virar N por produto na Fase 3 quando o item é lote/form-controlado e
 * a quantidade corrigida vem de mais de 1 lote do Aerosys -- ver
 * _montarItensSubmissao). offset/totalItens do addon contam nestas linhas
 * FLAT, não em produtos -- por isso NotaPendente.itensOriginais usa este tipo,
 * não ItemPreviewNota, senão a retomada (que faz itensOriginais.slice(offset))
 * ficaria dessincronizada assim que algum produto virasse mais de 1 linha.
 */
interface ItemSubmissaoNota {
    codigo: string;
    qtde: number;
    local: string;
    codVolume: string;
    /** Fase 3 (lote/form) -- CONTROLE (ICO_FORM_NUMERO, fallback ICO_FORM_LOTE) do lote alocado a ESTA linha. */
    controle?: string;
    /** Fase 2 (serial) -- só quando o produto é TIPCONTEST='E'. */
    seriais?: string[];
}

/**
 * Uma nota de correção INCOMPLETA (algum lote ou a finalização falhou no meio do
 * caminho -- ver comentário grande em `notasPendentes` no componente). Fica na
 * lista até "Retomar" completar ela ou o usuário "Descartar" o rastreio.
 * Independente por NUNOTA -- o usuário pode ter notas pendentes de VÁRIAS
 * empresas ao mesmo tempo (ex.: gerou pra empresa 4 e deu erro, foi pra empresa 1
 * gerar outra) sem que uma atrapalhe a outra.
 */
interface NotaPendente {
    nunota: number;
    codemp: number;
    /** FLAT (1+ por produto, ver ItemSubmissaoNota) -- mesma unidade de contagem do offset abaixo. */
    itensOriginais: ItemSubmissaoNota[];
    /** Quantas LINHAS (não produtos) já foram confirmadas gravadas (cumulativo entre tentativas). */
    offset: number;
    erro: string;
}

/**
 * Tela de Comparação de Estoque (Aerosys x Sankhya).
 *
 * IMPORTANTE — diferente de todo o resto do app (Separação/Conferência/
 * Logística/Itens Pendentes), que segue o padrão "fonte única por vez"
 * (Sankhya OU Legado, nunca junto), esta tela busca as DUAS fontes ao mesmo
 * tempo e casa por APE_ID (Aerosys) = CODPROD (Sankhya) — o objetivo é
 * justamente comparar, não escolher uma fonte.
 *
 * Filtro de empresa: os IDs de empresa do Aerosys e o CODEMP do Sankhya NÃO
 * correspondem um ao outro (não existe mapeamento 1:1 confiável — decisão
 * confirmada com o cliente). Por isso os dois seletores de empresa abaixo
 * são INDEPENDENTES: o usuário escolhe manualmente de cada lado. IMPORTANTE:
 * este componente usa apenas a LISTA de empresas do EmpresaStateService
 * (`empresaState.opcoes`), nunca `empresaState.selecionada` — essa é a
 * empresa GLOBAL do app (usada no header, em Pedido de Venda, etc.), e
 * trocar o filtro aqui não pode mudar a empresa do resto da aplicação.
 */
// @ts-ignore
@Component({
    selector: 'app-comparacao-estoque',
    standalone: true,
    imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
    templateUrl: './comparacao-estoque.component.html',
    styleUrl: './comparacao-estoque.component.css'
})
export class ComparacaoEstoqueComponent implements OnInit {

    // ── Filtros de empresa (independentes) ──────────────────────────────
    empresasAerosys: AerosysEmpresa[] = [];
    empresasAerosysCarregando = false;
    empresaAerosysSelecionada: AerosysEmpresa | null = null;

    codEmpSankhyaSelecionado = '';

    // ── Busca / filtro de texto ──────────────────────────────────────────
    somenteDivergentes = false;
    /** Esconde linhas onde o Aerosys não tem saldo (EST_QTDE <= 0 ou produto ausente no Aerosys). */
    somenteComEstoqueAerosys = false;
    filtroTexto = '';
    private _debounceFiltro: ReturnType<typeof setTimeout> | null = null;

    /**
     * "Comparar por" -- qual critério define se uma linha é "divergente". Os 4
     * critérios já vêm todos calculados numa busca só (ver ComparacaoEstoqueComponent
     * no backend), então trocar aqui é instantâneo -- NÃO refaz a busca (que é cara,
     * ~40 mil produtos nos dois sistemas).
     */
    tipoComparacao: TipoComparacao = 'ESTOQUE';
    readonly opcoesTipoComparacao: { valor: TipoComparacao; label: string }[] = [
        {valor: 'ESTOQUE', label: 'Saldo de Estoque'},
        {valor: 'VALOR', label: 'Preço / Valor'},
        {valor: 'ATIVO', label: 'Produto Ativo/Inativo'},
        {valor: 'PN', label: 'PN / Referência'},
        {valor: 'LOCAL', label: 'Local'},
        {valor: 'SERIE', label: 'Série'},
        {valor: 'LOTE', label: 'Lote / Form'},
    ];

    // ── Resultado ─────────────────────────────────────────────────────
    carregando = false;
    erro = '';
    jaBuscou = false;
    resultado: ComparacaoEstoqueResposta | null = null;

    /**
     * IMPORTANTE — performance: com ~40 mil produtos, renderizar TODAS as linhas de
     * uma vez deixa o navegador travado (DOM gigante + o Angular reavalia bindings a
     * cada ciclo de change detection). Duas medidas juntas resolvem isso:
     * 1) `linhasFiltradas` deixou de ser um getter que refiltra os 40 mil itens a
     *    cada ciclo -- agora é um cache (`_linhasFiltradasCache`) recalculado só
     *    quando o resultado ou os filtros realmente mudam (`_recalcularFiltro`).
     * 2) A tabela pagina o cache já filtrado (`linhasPaginadas`), então o DOM nunca
     *    tem mais que `itensPorPagina` linhas ao mesmo tempo.
     */
    private _linhasFiltradasCache: ComparacaoEstoqueItem[] = [];
    paginaAtual = 1;
    itensPorPagina = 100;
    readonly opcoesItensPorPagina = [50, 100, 200, 500];

    // ── Seletor de colunas (persistido) ─────────────────────────────────
    seletorColunasAberto: 'aerosys' | 'sankhya' | null = null;
    colunasAerosysVisiveis = new Set<string>();
    colunasSankhyaVisiveis = new Set<string>();

    private static readonly STORAGE_COLUNAS_AEROSYS = 'gpl_comparacao_estoque_colunas_aerosys';
    private static readonly STORAGE_COLUNAS_SANKHYA = 'gpl_comparacao_estoque_colunas_sankhya';

    /**
     * Colunas visíveis por padrão na primeira vez que a tela é usada (antes de qualquer
     * personalização salva). APE_ID/CODPROD ficam de fora do padrão de propósito -- eles
     * duplicam a coluna fixa "Código" à esquerda (é a própria chave de casamento
     * APE_ID=CODPROD), então mostrá-los de novo aqui só ocupa espaço à toa. Continuam
     * disponíveis no seletor de colunas pra quem quiser.
     */
    private static readonly COLUNAS_AEROSYS_PADRAO = ['APE_DESCRICAO', 'APE_PN', 'EST_QTDE', 'EST_LOCAL', 'APE_ATIVO'];
    private static readonly COLUNAS_SANKHYA_PADRAO = ['DESCRPROD', 'REFFORN', 'ESTOQUE', 'DESCRLOCAL', 'PROD_ATIVO'];

    // ── Nota de Correção (gera IMPLANTAÇÃO DE SALDOS DE ESTOQUE no Sankhya) ──
    //
    // Fase 1 (escopo combinado com o cliente): corrige só QUANTIDADE e LOCAL,
    // sempre no sentido Aerosys -> Sankhya (Aerosys é a fonte da verdade).
    // Form/serial/lote e correção de VALOR ficam para uma Fase 2. A prévia
    // roda 100% no navegador (os dois lados já estão carregados aqui, não
    // precisa ida ao banco) -- só quando o usuário confirma no modal é que o
    // backend é chamado (mesmo padrão em lotes de iniciar/processar/finalizar
    // já usado na Importação de Estoque legada, ver `EstoqueService`).
    //
    // Por que só permite gerar com UMA empresa Sankhya escolhida no filtro:
    // o cabeçalho da nota (TGFCAB) exige CODEMP explícito, e misturar itens de
    // empresas diferentes na mesma nota não faz sentido contábil.
    selecionados = new Set<string>();
    mostrarModalNota = false;
    /** 1 linha por produto -- o que o usuário VÊ na prévia. */
    itensPreviewNota: ItemPreviewNota[] = [];
    /** FLAT (1+ por produto) -- o que é REALMENTE enviado ao addon, ver _montarItensSubmissao. */
    itensSubmissaoNota: ItemSubmissaoNota[] = [];
    /** Fase 2/3: true enquanto _resolverPreviewAvancado() busca TIPCONTEST + seriais/forms disponíveis no Aerosys. */
    carregandoSeriais = false;
    gerandoNota = false;
    progressoNota = 0;
    notaEtapa = '';
    notaResultado: { nunota: number; totalItens: number } | null = null;
    notaErro = '';
    notaItensNaoImportados: any[] = [];

    /**
     * Notas de correção incompletas (incidente real: nota 2477, 3519 itens
     * selecionados, um lote/finalização bateu 500 no meio do caminho e só 1897
     * itens chegaram a ser gravados). Em vez de reprocessar TUDO de novo (o que
     * duplicaria os 1897 já gravados) ou criar outra nota (ficaria com duas notas
     * pra corrigir os mesmos produtos), guardamos aqui cada nota incompleta com
     * quantos itens da lista ORIGINAL já foram confirmados, pra "Retomar" mandar
     * só o restante pro MESMO nunota (ver iniciarNotaCorrecao no addon:
     * nunotaExistente). É uma LISTA (não um único slot) de propósito: o fluxo de
     * trabalho real é gerar nota pra empresa 4, depois pra empresa 1, etc. -- se
     * a empresa 4 der problema no meio, isso NÃO pode travar/perder o rastreio
     * enquanto o usuário segue gerando pra outras empresas.
     */
    notasPendentes: NotaPendente[] = [];
    /** NUNOTA sendo processado agora (pra saber em qual linha da lista mostrar o progresso). */
    nunotaProcessando: number | null = null;

    // ── Nota de Correção: documentos (PDF via ICO_FORM_CAMINHO) ──────────
    //
    // Só documentação/rastreabilidade (anexo na nota gerada, GPL_ANEXO_VENDA)
    // -- NÃO é fonte de TGFSER/CONTROLE (esses continuam vindo só de
    // seriais/forms-disponiveis, ver _resolverSeriaisPreview/
    // _resolverFormsPreview acima). Roda em paralelo com Fase 2/3 (ver
    // _resolverPreviewAvancado), e o anexo em si só acontece DEPOIS que a
    // nota terminou de ser gerada com sucesso (precisa do NUNOTA final).
    previewPdfUrl: SafeResourceUrl | null = null;
    /** URL "crua" do blob (não sanitizada) -- guardada separada só pra dar revokeObjectURL nela ao fechar. */
    private _previewPdfObjectUrl: string | null = null;
    previewPdfNome = '';
    previewPdfCarregando = false;
    previewPdfErro = '';
    anexandoDocumentos = false;
    notaAnexosResultado: { total: number; sucesso: number; falha: number } | null = null;

    constructor(
        private readonly comparacaoEstoqueService: ComparacaoEstoqueService,
        private readonly aerosysGateway: AerosysGatewayService,
        private readonly estoqueService: EstoqueService,
        private readonly sankhyaGateway: SankhyaGatewayService,
        private readonly sanitizer: DomSanitizer,
        readonly empresaState: EmpresaStateService,
    ) {
    }

    ngOnInit(): void {
        this._carregarColunasSalvas();
        void this.empresaState.carregar();
        this._carregarEmpresasAerosys();
    }

    private _carregarEmpresasAerosys(): void {
        this.empresasAerosysCarregando = true;
        this.aerosysGateway.listarEmpresas().subscribe({
            next: (todas) => {
                this.empresasAerosys = (todas || []).slice().sort((a, b) => Number(a.id) - Number(b.id));
                this.empresasAerosysCarregando = false;
            },
            error: () => {
                this.empresasAerosysCarregando = false;
            }
        });
    }

    private _carregarColunasSalvas(): void {
        const aerosysSalvas = this._lerColunasSalvas(ComparacaoEstoqueComponent.STORAGE_COLUNAS_AEROSYS);
        const sankhyaSalvas = this._lerColunasSalvas(ComparacaoEstoqueComponent.STORAGE_COLUNAS_SANKHYA);
        this.colunasAerosysVisiveis = new Set(aerosysSalvas ?? ComparacaoEstoqueComponent.COLUNAS_AEROSYS_PADRAO);
        this.colunasSankhyaVisiveis = new Set(sankhyaSalvas ?? ComparacaoEstoqueComponent.COLUNAS_SANKHYA_PADRAO);
    }

    private _lerColunasSalvas(chave: string): string[] | null {
        try {
            const bruto = localStorage.getItem(chave);
            if (!bruto) return null;
            const lista = JSON.parse(bruto);
            return Array.isArray(lista) ? lista : null;
        } catch {
            return null;
        }
    }

    private _salvarColunas(lado: 'aerosys' | 'sankhya'): void {
        const chave = lado === 'aerosys' ? ComparacaoEstoqueComponent.STORAGE_COLUNAS_AEROSYS : ComparacaoEstoqueComponent.STORAGE_COLUNAS_SANKHYA;
        const conjunto = lado === 'aerosys' ? this.colunasAerosysVisiveis : this.colunasSankhyaVisiveis;
        try {
            localStorage.setItem(chave, JSON.stringify(Array.from(conjunto)));
        } catch {
            // localStorage indisponível — segue só em memória nesta sessão.
        }
    }

    // ── Ações ─────────────────────────────────────────────────────────

    buscar(): void {
        this.carregando = true;
        this.erro = '';
        this.jaBuscou = true;
        this.selecionados.clear();
        // NÃO mexe em notasPendentes aqui -- trocar de filtro/empresa não pode
        // fazer perder o rastreio de notas incompletas de OUTRAS empresas.
        const empIdAerosys = this.empresaAerosysSelecionada ? Number(this.empresaAerosysSelecionada.id) : null;
        const codEmpSankhya = this.codEmpSankhyaSelecionado || null;

        this.comparacaoEstoqueService.comparar(empIdAerosys, codEmpSankhya).subscribe({
            next: (resposta) => {
                this.resultado = resposta;
                this.carregando = false;
                this._recalcularFiltro();
            },
            error: (err) => {
                this.erro = this._extrairMensagemErro(err);
                this.carregando = false;
            }
        });
    }

    /**
     * O backend (ComparacaoEstoqueController) devolve o erro como STRING pura no
     * corpo (`ResponseEntity.badRequest().body("Erro ao comparar...")`), não como
     * objeto `{detail: ...}`. Sem tratar esse caso, `err?.error?.detail` dava
     * undefined e a tela mostrava só a mensagem genérica do Angular ("Http
     * failure response...: 400 OK"), escondendo a causa real do erro.
     */
    private _extrairMensagemErro(err: any): string {
        if (err?.error?.detail) return err.error.detail;
        if (typeof err?.error === 'string' && err.error.trim()) return err.error;
        return err?.message ?? 'Erro ao buscar comparação de estoque.';
    }

    limparFiltros(): void {
        this.empresaAerosysSelecionada = null;
        this.codEmpSankhyaSelecionado = '';
        this.somenteDivergentes = false;
        this.filtroTexto = '';
        this.selecionados.clear();
        this._recalcularFiltro();
    }

    onSomenteDivergentesChange(valor: boolean): void {
        this.somenteDivergentes = valor;
        this._recalcularFiltro();
    }

    onSomenteComEstoqueAerosysChange(valor: boolean): void {
        this.somenteComEstoqueAerosys = valor;
        this._recalcularFiltro();
    }

    onTipoComparacaoChange(valor: TipoComparacao): void {
        this.tipoComparacao = valor;
        this._recalcularFiltro();
    }

    /** Flag de divergência do item de acordo com o critério selecionado em `tipoComparacao`. */
    itemDivergente(item: ComparacaoEstoqueItem): boolean {
        switch (this.tipoComparacao) {
            case 'VALOR': return item.divergenteValor;
            case 'ATIVO': return item.divergenteAtivo;
            case 'PN': return item.divergentePn;
            case 'LOCAL': return item.divergenteLocal;
            case 'SERIE': return item.divergenteSerie;
            case 'LOTE': return item.divergenteLote;
            default: return item.divergenteEstoque;
        }
    }

    /** Total de divergentes (já calculado no backend) de acordo com o critério selecionado. */
    get totalDivergentesAtual(): number {
        if (!this.resultado) return 0;
        switch (this.tipoComparacao) {
            case 'VALOR': return this.resultado.totalDivergentesValor;
            case 'ATIVO': return this.resultado.totalDivergentesAtivo;
            case 'PN': return this.resultado.totalDivergentesPn;
            case 'LOCAL': return this.resultado.totalDivergentesLocal;
            case 'SERIE': return this.resultado.totalDivergentesSerie;
            case 'LOTE': return this.resultado.totalDivergentesLote;
            default: return this.resultado.totalDivergentesEstoque;
        }
    }

    /** true quando o critério selecionado é Série/Lote mas o backend não conseguiu calcular (faltou empresa ou deu erro no Aerosys). */
    get avisoContagemSerieLoteIndisponivel(): boolean {
        return !!this.resultado
            && (this.tipoComparacao === 'SERIE' || this.tipoComparacao === 'LOTE')
            && !this.resultado.contagemSerieLoteDisponivel;
    }

    /** Debounced -- recalcular a cada tecla digitada travaria a digitação com 40 mil linhas. */
    onFiltroTextoChange(valor: string): void {
        this.filtroTexto = valor;
        if (this._debounceFiltro) clearTimeout(this._debounceFiltro);
        this._debounceFiltro = setTimeout(() => this._recalcularFiltro(), 300);
    }

    onEmpresaAerosysChange(id: number | null): void {
        this.empresaAerosysSelecionada = id == null ? null : (this.empresasAerosys.find(e => Number(e.id) === Number(id)) ?? null);
    }

    toggleSeletorColunas(lado: 'aerosys' | 'sankhya'): void {
        this.seletorColunasAberto = this.seletorColunasAberto === lado ? null : lado;
    }

    fecharSeletorColunas(): void {
        this.seletorColunasAberto = null;
    }

    colunaVisivel(lado: 'aerosys' | 'sankhya', campo: string): boolean {
        return lado === 'aerosys' ? this.colunasAerosysVisiveis.has(campo) : this.colunasSankhyaVisiveis.has(campo);
    }

    alternarColuna(lado: 'aerosys' | 'sankhya', campo: string): void {
        const conjunto = lado === 'aerosys' ? this.colunasAerosysVisiveis : this.colunasSankhyaVisiveis;
        if (conjunto.has(campo)) {
            conjunto.delete(campo);
        } else {
            conjunto.add(campo);
        }
        this._salvarColunas(lado);
    }

    get colunasAerosysVisiveisOrdenadas(): string[] {
        if (!this.resultado) return [];
        return this.resultado.camposAerosys.filter(c => this.colunasAerosysVisiveis.has(c));
    }

    get colunasSankhyaVisiveisOrdenadas(): string[] {
        if (!this.resultado) return [];
        return this.resultado.camposSankhya.filter(c => this.colunasSankhyaVisiveis.has(c));
    }

    /** Leitura barata do cache -- não refiltra nada (ver _recalcularFiltro). */
    get linhasFiltradas(): ComparacaoEstoqueItem[] {
        return this._linhasFiltradasCache;
    }

    private _recalcularFiltro(): void {
        if (!this.resultado) {
            this._linhasFiltradasCache = [];
        } else {
            let lista = this.resultado.linhas;
            if (this.somenteDivergentes) {
                lista = lista.filter(i => this.itemDivergente(i));
            }
            if (this.somenteComEstoqueAerosys) {
                lista = lista.filter(i => (i.qtdeAerosys ?? 0) > 0);
            }
            const termo = this.filtroTexto.trim().toUpperCase();
            if (termo) {
                lista = lista.filter(i => this._itemBate(i, termo));
            }
            this._linhasFiltradasCache = lista;
        }
        this.paginaAtual = 1;
    }

    // ── Paginação ─────────────────────────────────────────────────────
    // Com até ~40 mil produtos, renderizar tudo de uma vez trava o navegador --
    // a tabela sempre mostra só a "fatia" da página atual sobre o cache já filtrado.

    get totalPaginas(): number {
        return Math.max(1, Math.ceil(this._linhasFiltradasCache.length / this.itensPorPagina));
    }

    get linhasPaginadas(): ComparacaoEstoqueItem[] {
        const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
        return this._linhasFiltradasCache.slice(inicio, inicio + this.itensPorPagina);
    }

    irParaPagina(pagina: number): void {
        this.paginaAtual = Math.min(Math.max(1, pagina), this.totalPaginas);
    }

    paginaAnterior(): void {
        this.irParaPagina(this.paginaAtual - 1);
    }

    proximaPagina(): void {
        this.irParaPagina(this.paginaAtual + 1);
    }

    onItensPorPaginaChange(valor: number): void {
        this.itensPorPagina = Number(valor);
        this.paginaAtual = 1;
    }

    trackByCodigo(_index: number, item: ComparacaoEstoqueItem): string {
        return item.codigo;
    }

    private _itemBate(item: ComparacaoEstoqueItem, termo: string): boolean {
        if (item.codigo.toUpperCase().includes(termo)) return true;
        const descAerosys = item.aerosys?.campos?.['APE_DESCRICAO'] ?? '';
        const pnAerosys = item.aerosys?.campos?.['APE_PN'] ?? '';
        const descSankhya = item.sankhya?.campos?.['DESCRPROD'] ?? '';
        const refSankhya = item.sankhya?.campos?.['REFFORN'] ?? '';
        return descAerosys.toUpperCase().includes(termo)
            || pnAerosys.toUpperCase().includes(termo)
            || descSankhya.toUpperCase().includes(termo)
            || refSankhya.toUpperCase().includes(termo);
    }

    campoAerosys(item: ComparacaoEstoqueItem, campo: string): string {
        return item.aerosys?.campos?.[campo] ?? '';
    }

    campoSankhya(item: ComparacaoEstoqueItem, campo: string): string {
        return item.sankhya?.campos?.[campo] ?? '';
    }

    /** Tooltip da coluna Lote/Form no modal de prévia -- lista cada lote alocado (Fase 3) + o aviso de sobra, se houver. */
    _tituloLote(p: ItemPreviewNota): string {
        const partes: string[] = [];
        if (p.alocacoesLote?.length) {
            partes.push(...p.alocacoesLote.map(a => `${a.controle}: ${a.qtde}`));
        }
        if (p.avisoLote) partes.push(p.avisoLote);
        return partes.join(' | ');
    }

    // ── Nota de Correção: seleção de linhas ──────────────────────────────

    /** Só faz sentido corrigir a partir de itens que existem no Aerosys (é a fonte da verdade). */
    itemElegivelParaNota(item: ComparacaoEstoqueItem): boolean {
        return !!item.aerosys?.existe;
    }

    estaSelecionado(codigo: string): boolean {
        return this.selecionados.has(codigo);
    }

    toggleSelecao(item: ComparacaoEstoqueItem): void {
        if (!this.itemElegivelParaNota(item)) return;
        if (this.selecionados.has(item.codigo)) {
            this.selecionados.delete(item.codigo);
        } else {
            this.selecionados.add(item.codigo);
        }
    }

    /** Seleciona/deseleciona todos os itens elegíveis DIVERGENTES da página atual. */
    toggleSelecionarPaginaDivergentes(): void {
        const elegiveisPagina = this.linhasPaginadas.filter(i => this.itemElegivelParaNota(i) && this.itemDivergente(i));
        const todosJaSelecionados = elegiveisPagina.length > 0 && elegiveisPagina.every(i => this.selecionados.has(i.codigo));
        if (todosJaSelecionados) {
            elegiveisPagina.forEach(i => this.selecionados.delete(i.codigo));
        } else {
            elegiveisPagina.forEach(i => this.selecionados.add(i.codigo));
        }
    }

    get totalSelecionados(): number {
        return this.selecionados.size;
    }

    limparSelecao(): void {
        this.selecionados.clear();
    }

    /**
     * Nota só pode ser gerada com UMA empresa Sankhya específica escolhida no filtro
     * (ver comentário acima). Gerar pra empresa 4 e depois pra empresa 1 (ou vice-versa)
     * é normal e sempre permitido -- ter uma nota pendente de UMA empresa não bloqueia
     * gerar nota NOVA pra OUTRA (ver notasPendentes, é uma lista independente por
     * NUNOTA). Só bloqueia enquanto uma geração/retomada já está rodando
     * (gerandoNota), pra não disparar duas chamadas em paralelo.
     */
    get podeGerarNotaCorrecao(): boolean {
        return this.totalSelecionados > 0 && !!this.codEmpSankhyaSelecionado && !this.gerandoNota;
    }

    /** Desiste de rastrear UMA nota incompleta nesta tela (ela CONTINUA existindo/incompleta no Sankhya -- resolva direto lá se necessário). */
    descartarNotaPendente(pendente: NotaPendente): void {
        this._removerNotaPendente(pendente.nunota);
    }

    private _removerNotaPendente(nunota: number): void {
        this.notasPendentes = this.notasPendentes.filter(p => p.nunota !== nunota);
    }

    // ── Nota de Correção: prévia + geração ───────────────────────────────

    abrirModalNota(): void {
        if (!this.resultado || !this.podeGerarNotaCorrecao) return;
        this.notaErro = '';
        this.notaResultado = null;
        this.notaItensNaoImportados = [];
        this.progressoNota = 0;
        this.notaEtapa = '';
        this.notaAnexosResultado = null;
        this.anexandoDocumentos = false;
        this._fecharPreviewPdfInterno();

        this.itensPreviewNota = this.resultado.linhas
            .filter(i => this.selecionados.has(i.codigo))
            .map(i => ({
                codigo: i.codigo,
                descricao: this.campoSankhya(i, 'DESCRPROD') || this.campoAerosys(i, 'APE_DESCRICAO') || '',
                qtdeAtual: i.qtdeSankhya,
                qtdeNova: i.qtdeAerosys,
                localAtual: this.campoSankhya(i, 'DESCRLOCAL') || '',
                localNovo: this.campoAerosys(i, 'EST_LOCAL') || '',
                codVolume: this.campoSankhya(i, 'CODVOL') || '',
            }));

        // Baseline 1:1 (sem serial/lote ainda) -- assim o botão "Confirmar" já tem o que
        // enviar mesmo se a resolução abaixo demorar ou falhar (fica travado só enquanto
        // carregandoSeriais=true, ver HTML).
        this._montarItensSubmissao();

        this.mostrarModalNota = true;
        // Roda em paralelo, sem travar a abertura do modal -- os itens são atualizados em
        // memória assim que a resolução termina (ver carregandoSeriais).
        this._resolverPreviewAvancado();
    }

    /**
     * Orquestra Fase 2 (seriais) + Fase 3 (lote/form) em paralelo -- são
     * mutuamente exclusivas por produto (TIPCONTEST só pode ser 'E' OU 'L',
     * nunca os dois), então não há disputa entre elas. Ao final de AMBAS,
     * reconstrói `itensSubmissaoNota` (que é o que de fato vai pro addon) a
     * partir do estado atualizado de `itensPreviewNota`.
     */
    private async _resolverPreviewAvancado(): Promise<void> {
        this.carregandoSeriais = true;
        try {
            await Promise.all([
                this._resolverSeriaisPreview(),
                this._resolverFormsPreview(),
                this._resolverDocumentosPreview(),
            ]);
        } finally {
            this._montarItensSubmissao();
            this.carregandoSeriais = false;
        }
    }

    /**
     * Documentos (PDF via ICO_FORM_CAMINHO, Aerosys) -- busca, pra cada item
     * selecionado, quais documentos de compra existem (independente de
     * TIPCONTEST, diferente de seriais/forms -- documento é anexo/
     * documentação, não fonte de dado). Pré-seleciona o mais recente de cada
     * item por padrão (o usuário pode trocar ou desmarcar no modal antes de
     * confirmar). Falha aqui NÃO bloqueia a geração da nota -- mesmo
     * tratamento "melhor esforço" das Fases 2/3 acima.
     */
    private async _resolverDocumentosPreview(): Promise<void> {
        const codigos = this.itensPreviewNota.map(p => p.codigo);
        if (!codigos.length) return;

        try {
            const documentosPorPeca = await firstValueFrom(
                this.comparacaoEstoqueService.buscarDocumentosDisponiveis(codigos));

            for (const p of this.itensPreviewNota) {
                const docs = documentosPorPeca[p.codigo] || [];
                p.documentos = docs;
                p.documentoEscolhidoIcoId = docs.length ? docs[0].icoId : null;
            }
        } catch (e: any) {
            console.error('[Documentos] Falha ao resolver documentos disponíveis', e);
        }
    }

    /** Troca/desmarca qual documento será anexado pra este item (null = não anexar nenhum). */
    selecionarDocumento(item: ItemPreviewNota, icoId: number | null): void {
        item.documentoEscolhidoIcoId = icoId;
    }

    /** Quantos itens têm um documento escolhido pra anexar -- usado no resumo do modal. */
    get totalDocumentosEscolhidos(): number {
        return this.itensPreviewNota.filter(p => p.documentoEscolhidoIcoId != null).length;
    }

    /** Preview do PDF (blob) num overlay simples -- não navega pra fora da tela. */
    async visualizarDocumento(icoId: number, nomeArquivo: string): Promise<void> {
        this.previewPdfErro = '';
        this.previewPdfCarregando = true;
        try {
            const blob = await firstValueFrom(this.comparacaoEstoqueService.baixarDocumentoPdf(icoId));
            this._fecharPreviewPdfInterno();
            this._previewPdfObjectUrl = URL.createObjectURL(blob);
            this.previewPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._previewPdfObjectUrl);
            this.previewPdfNome = nomeArquivo;
        } catch (e: any) {
            this.previewPdfErro = e?.status === 404
                ? 'Documento não encontrado no servidor.'
                : (e?.message || 'Falha ao carregar o PDF.');
        } finally {
            this.previewPdfCarregando = false;
        }
    }

    fecharPreviewPdf(): void {
        this._fecharPreviewPdfInterno();
    }

    private _fecharPreviewPdfInterno(): void {
        if (this._previewPdfObjectUrl) {
            URL.revokeObjectURL(this._previewPdfObjectUrl);
        }
        this._previewPdfObjectUrl = null;
        this.previewPdfUrl = null;
        this.previewPdfNome = '';
        this.previewPdfErro = '';
    }

    /**
     * Converte um Blob em base64 "puro" (sem o prefixo "data:...;base64,") --
     * mesmo padrão já usado em processarImagem() (duplicar-produto.component.ts)
     * pra upload de imagem, reaproveitado aqui pro upload de anexo.
     */
    private _blobParaBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const resultado = reader.result as string;
                const virgula = resultado.indexOf(',');
                resolve(virgula >= 0 ? resultado.substring(virgula + 1) : resultado);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Anexa os documentos escolhidos (documentoEscolhidoIcoId de cada item)
     * na nota JÁ GERADA com sucesso, via GPL_ANEXO_VENDA (mesmo mecanismo do
     * Pedido de Venda -- ver anexarArquivoVenda, funciona com qualquer NUNOTA
     * de TGFCAB, não só Pedido de Venda). Roda DEPOIS da nota confirmada
     * (precisa do NUNOTA final) -- best-effort: falha em anexar NÃO desfaz
     * nem marca a nota como falha, ela já foi gravada com sucesso; só o
     * resumo (notaAnexosResultado) reporta quantos anexos falharam, pra
     * revisão manual depois se precisar.
     */
    private async _anexarDocumentosNaNota(nunota: number): Promise<void> {
        const itensComDocumento = this.itensPreviewNota.filter(p => p.documentoEscolhidoIcoId != null);
        if (!itensComDocumento.length) return;

        this.anexandoDocumentos = true;
        this.notaAnexosResultado = {total: itensComDocumento.length, sucesso: 0, falha: 0};

        for (const item of itensComDocumento) {
            const icoId = item.documentoEscolhidoIcoId!;
            try {
                const blob = await firstValueFrom(this.comparacaoEstoqueService.baixarDocumentoPdf(icoId));
                const base64 = await this._blobParaBase64(blob);
                const nomeArquivo = `documento_${item.codigo}_${icoId}.pdf`;
                const descricao = `Nota de Correção de Estoque -- documento de compra da peça ${item.codigo} (${item.descricao || ''})`.trim();
                await firstValueFrom(this.sankhyaGateway.anexarArquivoVenda(nunota, nomeArquivo, base64, descricao));
                this.notaAnexosResultado.sucesso++;
            } catch (e: any) {
                console.error(`[Documentos] Falha ao anexar documento icoId=${icoId} na nota ${nunota}`, e);
                this.notaAnexosResultado.falha++;
            }
        }

        this.anexandoDocumentos = false;
    }

    /**
     * Fase 2 (seriais): descobre quais itens selecionados são serial-
     * controlados (TIPCONTEST='E' em TGFPRO, consulta independente de TGFEST
     * -- ver comentário no backend) e, pra esses, busca no Aerosys os
     * seriais disponíveis (ICS_STATUS='N') da empresa Aerosys selecionada no
     * filtro. Anexa os N primeiros (FIFO, já vem ordenado assim do backend)
     * — N = quantidade corrigida (qtdeNova) arredondada — em `seriais` de
     * cada item.
     *
     * "Gera o que achar e avisa" (decisão explícita): se o Aerosys tiver
     * MENOS seriais disponíveis que a quantidade corrigida, ou se nenhuma
     * empresa Aerosys específica estiver selecionada no filtro (não dá pra
     * saber de qual empresa puxar o serial), o item fica com `avisoSerial`
     * preenchido mas SEM bloquear a geração da nota — só o vínculo de série
     * fica faltando pra esses itens, revisável depois na tela nativa.
     */
    private async _resolverSeriaisPreview(): Promise<void> {
        const codigos = this.itensPreviewNota.map(p => p.codigo);
        if (!codigos.length) return;

        try {
            const tipoControle = await firstValueFrom(this.comparacaoEstoqueService.buscarTipoControle(codigos));
            const itensSerial = this.itensPreviewNota.filter(p => tipoControle[p.codigo] === 'E');
            if (!itensSerial.length) return;

            const empIdAerosys = this.empresaAerosysSelecionada ? Number(this.empresaAerosysSelecionada.id) : null;
            if (empIdAerosys == null) {
                itensSerial.forEach(p => {
                    p.avisoSerial = 'Produto controla série, mas nenhuma empresa Aerosys específica está selecionada no filtro -- selecione uma pra atribuir os seriais automaticamente.';
                });
                return;
            }

            const codigosSerial = itensSerial.map(p => p.codigo);
            const seriaisDisponiveis = await firstValueFrom(
                this.comparacaoEstoqueService.buscarSeriaisDisponiveis(codigosSerial, empIdAerosys));

            for (const p of itensSerial) {
                const disponiveis: any[] = seriaisDisponiveis[p.codigo] || [];
                const qtdeNecessaria = Math.max(0, Math.round(p.qtdeNova ?? 0));
                const escolhidos = disponiveis.slice(0, qtdeNecessaria).map(s => s.icsNs).filter((ns: any) => !!ns);
                p.seriais = escolhidos;
                if (escolhidos.length < qtdeNecessaria) {
                    p.avisoSerial = `Só ${escolhidos.length} de ${qtdeNecessaria} série(s) disponível(is) no Aerosys -- a nota será gerada mesmo assim, o restante fica sem série atribuída.`;
                }
            }
        } catch (e: any) {
            // Falha ao resolver seriais NÃO bloqueia a nota (Fase 1 continua funcionando normalmente,
            // é só o vínculo de série que fica de fora, sem `avisoSerial` específico por item aqui --
            // o console.error já registra o motivo real pra investigação).
            console.error('[Fase 2] Falha ao resolver seriais disponíveis', e);
        }
    }

    /**
     * Fase 3 (lote/form): descobre quais itens selecionados são lote-
     * controlados (TIPCONTEST='L') e, pra esses, busca no Aerosys o saldo de
     * FORM disponível (já em ordem FIFO -- lote mais antigo primeiro) da
     * empresa Aerosys selecionada. Consome os lotes em ordem até cobrir a
     * quantidade corrigida (qtdeNova) -- PODE vir de mais de 1 lote, e cada
     * um vira sua PRÓPRIA alocação em `alocacoesLote` (decisão explícita do
     * cliente: não pode perder rastreabilidade de nenhum form na correção,
     * já que TGFITE.CONTROLE só guarda 1 valor por linha -- ver
     * _montarItensSubmissao, que transforma isso em N linhas reais).
     *
     * "Gera o que achar e avisa": se a soma dos saldos disponíveis não
     * cobrir toda a qtdeNova, ou se nenhuma empresa Aerosys estiver
     * selecionada, o item fica com `avisoLote` preenchido mas SEM bloquear a
     * geração -- a sobra vira uma linha sem CONTROLE (mesmo comportamento da
     * Fase 1), revisável depois manualmente.
     */
    private async _resolverFormsPreview(): Promise<void> {
        const codigos = this.itensPreviewNota.map(p => p.codigo);
        if (!codigos.length) return;

        try {
            const tipoControle = await firstValueFrom(this.comparacaoEstoqueService.buscarTipoControle(codigos));
            const itensLote = this.itensPreviewNota.filter(p => tipoControle[p.codigo] === 'L');
            if (!itensLote.length) return;

            const empIdAerosys = this.empresaAerosysSelecionada ? Number(this.empresaAerosysSelecionada.id) : null;
            if (empIdAerosys == null) {
                itensLote.forEach(p => {
                    p.avisoLote = 'Produto controla lote/form, mas nenhuma empresa Aerosys específica está selecionada no filtro -- selecione uma pra atribuir os lotes automaticamente.';
                });
                return;
            }

            const codigosLote = itensLote.map(p => p.codigo);
            const formsDisponiveis = await firstValueFrom(
                this.comparacaoEstoqueService.buscarFormsDisponiveis(codigosLote, empIdAerosys));

            for (const p of itensLote) {
                const lotes: any[] = formsDisponiveis[p.codigo] || [];
                let restante = Math.round(p.qtdeNova ?? 0);
                const alocacoes: { controle: string; qtde: number }[] = [];

                for (const lote of lotes) {
                    if (restante <= 0) break;
                    const saldoLote = Math.max(0, Math.round(Number(lote.saldo) || 0));
                    if (saldoLote <= 0) continue;
                    const controle = lote.icoFormNumero || lote.icoFormLote;
                    if (!controle) continue; // sem form/lote identificável -- pula, não dá pra gravar CONTROLE vazio com propósito
                    const consumido = Math.min(saldoLote, restante);
                    alocacoes.push({controle: String(controle), qtde: consumido});
                    restante -= consumido;
                }

                p.alocacoesLote = alocacoes;
                if (restante > 0) {
                    p.avisoLote = `Aerosys só tinha saldo de form/lote pra ${Math.round((p.qtdeNova ?? 0) - restante)} de ${Math.round(p.qtdeNova ?? 0)} unidade(s) -- a nota será gerada mesmo assim, o restante fica sem lote atribuído.`;
                }
            }
        } catch (e: any) {
            console.error('[Fase 3] Falha ao resolver forms/lotes disponíveis', e);
        }
    }

    /**
     * Expande `itensPreviewNota` (1 linha por produto, o que o usuário VÊ) em
     * `itensSubmissaoNota` (1+ linha por produto, o que É REALMENTE enviado
     * ao addon) -- ver comentário no tipo ItemSubmissaoNota sobre por que
     * isso precisa ser uma lista separada (offset/retomada contam linhas
     * flat, não produtos).
     */
    private _montarItensSubmissao(): void {
        const linhas: ItemSubmissaoNota[] = [];
        for (const p of this.itensPreviewNota) {
            if (p.alocacoesLote && p.alocacoesLote.length) {
                for (const aloc of p.alocacoesLote) {
                    linhas.push({
                        codigo: p.codigo,
                        qtde: aloc.qtde,
                        local: p.localNovo,
                        codVolume: p.codVolume,
                        controle: aloc.controle,
                    });
                }
                // Sobra sem lote (Aerosys sem saldo suficiente) -- 1 linha extra sem CONTROLE,
                // pra não perder quantidade (mesma filosofia "gera o que achar e avisa" da Fase 1).
                const totalAlocado = p.alocacoesLote.reduce((soma, a) => soma + a.qtde, 0);
                const restante = Math.round((p.qtdeNova ?? 0) - totalAlocado);
                if (restante > 0) {
                    linhas.push({codigo: p.codigo, qtde: restante, local: p.localNovo, codVolume: p.codVolume});
                }
            } else {
                linhas.push({
                    codigo: p.codigo,
                    qtde: p.qtdeNova ?? 0,
                    local: p.localNovo,
                    codVolume: p.codVolume,
                    seriais: p.seriais && p.seriais.length ? p.seriais : undefined,
                });
            }
        }
        this.itensSubmissaoNota = linhas;
    }

    fecharModalNota(): void {
        if (this.gerandoNota) return; // não fecha no meio de uma geração em andamento
        this.mostrarModalNota = false;
        this._fecharPreviewPdfInterno();
    }

    /**
     * Confirma e gera a nota NOVA a partir da seleção atual. Reaproveita o MESMO
     * fluxo em lotes (iniciar -> N lotes de itens -> finalizar) já usado pela
     * Importação de Estoque legada (ver duplicar-produto.component.ts/executarNota).
     * O codemp fica "travado" no momento da chamada (não lê o filtro de novo depois),
     * pra não se confundir se o usuário trocar de empresa enquanto isso roda.
     */
    async confirmarGerarNota(): Promise<void> {
        if (!this.itensSubmissaoNota.length) return;
        const codemp = Number(this.codEmpSankhyaSelecionado);
        this.notaResultado = null;
        this.notaErro = '';
        // anexarDocumentos=true só aqui: itensPreviewNota está fresco/relevante
        // pra ESTA geração (o usuário acabou de revisar no modal). Na retomada
        // (abaixo) itensPreviewNota pode não corresponder à nota sendo
        // retomada -- ver retomarNotaEmAndamento.
        await this._executarLotesNota(null, this.itensSubmissaoNota, codemp, true);
    }

    /**
     * Retoma uma nota específica que ficou incompleta (ver NotaPendente/notasPendentes).
     * Manda pro addon SÓ os itens que ainda não foram confirmados (a partir do offset
     * cumulativo já processado), usando nunotaExistente pra NÃO criar cabeçalho novo.
     * Se já não sobra nenhum item (só a finalização falhou), pula direto pro finalizar.
     */
    async retomarNotaEmAndamento(pendente: NotaPendente): Promise<void> {
        if (this.gerandoNota) return;
        const restantes = pendente.itensOriginais.slice(pendente.offset);

        if (!restantes.length) {
            // Todos os itens já foram gravados -- só a finalização (que é trivial,
            // só libera memória) que falhou da última vez. Repete só ela.
            this.gerandoNota = true;
            this.nunotaProcessando = pendente.nunota;
            pendente.erro = '';
            this.notaEtapa = 'Finalizando...';
            try {
                const resFim: any = await firstValueFrom(this.estoqueService.finalizarNotaCorrecao({nunota: pendente.nunota}));
                if (resFim?.sucesso) {
                    this._removerNotaPendente(pendente.nunota);
                } else {
                    pendente.erro = `Falhou ao finalizar de novo -- ${resFim?.erro || 'erro desconhecido'}`;
                }
            } catch (e: any) {
                pendente.erro = e?.message || 'Falha de comunicação ao finalizar.';
            }
            this.notaEtapa = '';
            this.nunotaProcessando = null;
            this.gerandoNota = false;
            return;
        }

        // anexarDocumentos=false: retomada roda fora do fluxo do modal (lista
        // de notas pendentes), itensPreviewNota não é garantidamente o mesmo
        // conjunto/nota -- não arrisca anexar documento no NUNOTA errado.
        await this._executarLotesNota(pendente, restantes, pendente.codemp, false);
    }

    /**
     * Núcleo comum de geração/retomada: iniciar (nota nova OU `pendente.nunota` já
     * existente) -> N lotes de itens -> finalizar. `pendente.offset` é atualizado
     * PROGRESSIVAMENTE (a cada lote confirmado), não só no final -- assim, se a JVM
     * do addon travar/estourar memória no meio do caminho (foi o que aconteceu na
     * nota 2477: 500 HTML sem JSON, parou em 1897 de 3519), o que já foi gravado
     * fica registrado em `notasPendentes` e "Retomar" manda só o restante, sem
     * duplicar nem recriar nota. Quando `pendente` é null (nota nova), a entrada é
     * criada em `notasPendentes` assim que o cabeçalho existe -- mesmo que o resto
     * falhe depois, ela já fica lá, rastreável.
     */
    private async _executarLotesNota(pendente: NotaPendente | null, itensParaEnviar: ItemSubmissaoNota[], codemp: number, anexarDocumentos: boolean = false): Promise<void> {
        this.gerandoNota = true;
        this.notaItensNaoImportados = [];
        this.progressoNota = 0;
        this.nunotaProcessando = pendente ? pendente.nunota : null;
        if (pendente) pendente.erro = '';

        // itensParaEnviar já é FLAT (1+ linha por produto, ver ItemSubmissaoNota/_montarItensSubmissao)
        // -- aqui é só remapear pro formato que o addon espera, sem nenhuma expansão.
        const itensPayload = itensParaEnviar.map(p => ({
            codProd: p.codigo,
            qtde: p.qtde,
            local: p.local,
            codVolume: p.codVolume || undefined,
            // Fase 3 (lote/form) -- undefined pros itens sem alocação (comportamento igual à Fase 1).
            controle: p.controle || undefined,
            // Fase 2 (seriais) -- undefined pros itens sem serial atribuído (comportamento igual à Fase 1).
            seriais: p.seriais && p.seriais.length ? p.seriais : undefined,
        }));
        const nunotaExistente = pendente ? pendente.nunota : null;

        try {
            this.notaEtapa = nunotaExistente ? `Retomando nota ${nunotaExistente}...` : 'Iniciando nota...';
            const resInicio: any = await firstValueFrom(this.estoqueService.iniciarNotaCorrecao({
                codemp, codparc: 1, itens: itensPayload,
                nunotaExistente: nunotaExistente || undefined,
            }));
            if (resInicio?.itensNaoImportados?.length) {
                this.notaItensNaoImportados.push(...resInicio.itensNaoImportados);
            }
            if (!resInicio?.sucesso || !resInicio?.nunota) {
                const msg = resInicio?.erro || 'Falha ao iniciar a nota de correção.';
                if (pendente) pendente.erro = msg; else this.notaErro = msg;
                this.gerandoNota = false;
                this.notaEtapa = '';
                this.nunotaProcessando = null;
                return;
            }

            const nunota = resInicio.nunota;
            this.nunotaProcessando = nunota;
            let ativo = pendente;
            if (!ativo) {
                // Nota nova: registra na lista assim que o cabeçalho existe, ANTES
                // de mandar qualquer item -- se falhar daqui pra frente, já fica
                // rastreável (é exatamente o que faltou no incidente da nota 2477).
                ativo = {nunota, codemp, itensOriginais: itensParaEnviar.slice(), offset: 0, erro: ''};
                this.notasPendentes.push(ativo);
            }

            const totalItensLote = resInicio.totalItens || 0;
            const totalGeral = ativo.itensOriginais.length || totalItensLote;
            // Reduzido de 50 -> 20 depois de DOIS incidentes de 500 HTML (notas 2477 e
            // 2478, empresa 4) em cargas de ~3500 itens -- cada item roda validação de
            // regra de negócio nativa completa via CACHelper, então lotes menores
            // significam menos trabalho acumulado por chamada e mais chance da JVM do
            // addon respirar (GC) entre uma chamada e outra. Ainda não temos a causa
            // raiz confirmada (falta log do servidor do addon), isso é mitigação, não
            // a correção definitiva.
            const TAMANHO_LOTE = 20;
            // Pequena pausa entre lotes -- dá um respiro pro addon (GC/conexões) em
            // vez de martelar requisição atrás de requisição sem parar.
            const PAUSA_ENTRE_LOTES_MS = 300;
            let offset = 0;
            let falhaLote = '';

            while (offset < totalItensLote) {
                this.notaEtapa = `Nota ${nunota}: gravando itens ${ativo.offset + offset} de ${totalGeral}...`;
                try {
                    const resLote: any = await firstValueFrom(
                        this.estoqueService.processarLoteItens({nunota, offset, tamanhoLote: TAMANHO_LOTE})
                    );
                    if (!resLote?.sucesso) {
                        falhaLote = resLote?.erro || `Falha ao processar lote a partir do item ${offset}.`;
                        break;
                    }
                    if (resLote?.itensNaoImportados?.length) {
                        this.notaItensNaoImportados.push(...resLote.itensNaoImportados);
                    }
                    const processados = resLote.processados || 0;
                    offset += processados;
                    ativo.offset += processados; // cumulativo -- sobrevive a falhas parciais
                    this.progressoNota = totalGeral > 0 ? Math.min(99, Math.round((ativo.offset / totalGeral) * 100)) : 99;
                    if (processados <= 0) break; // segurança contra loop infinito
                    if (offset < totalItensLote) {
                        await new Promise(resolve => setTimeout(resolve, PAUSA_ENTRE_LOTES_MS));
                    }
                } catch (e: any) {
                    falhaLote = e?.message || `Falha de comunicação no lote a partir do item ${offset}.`;
                    break;
                }
            }

            this.notaEtapa = `Nota ${nunota}: finalizando...`;
            const resFim: any = await firstValueFrom(this.estoqueService.finalizarNotaCorrecao({nunota}));
            if (resFim?.sucesso) {
                this.progressoNota = 100;
                this.selecionados.clear();
                if (falhaLote) {
                    ativo.erro = falhaLote; // ainda tem itens restantes -- continua rastreada
                    this.notaErro = falhaLote; // espelha na modal (se estiver aberta)
                } else {
                    this._removerNotaPendente(nunota); // completa -- some da lista de pendentes
                    this.notaResultado = {nunota, totalItens: totalGeral};
                    // Documentos (anexo, GPL_ANEXO_VENDA) -- só depois da nota
                    // 100% confirmada, e só no fluxo de geração fresca (ver
                    // comentário em confirmarGerarNota/retomarNotaEmAndamento).
                    // Best-effort: não derruba o resultado de sucesso já obtido.
                    if (anexarDocumentos) {
                        await this._anexarDocumentosNaNota(nunota);
                    }
                }
            } else {
                const msg = [falhaLote, `Falhou ao finalizar -- ${resFim?.erro || 'erro desconhecido'}`].filter(Boolean).join(' | ');
                ativo.erro = msg;
                this.notaErro = msg;
            }
        } catch (e: any) {
            const msg = e?.message || 'Falha de comunicação ao gerar a nota de correção.';
            if (pendente) pendente.erro = msg; else this.notaErro = msg;
        }

        this.notaEtapa = '';
        this.nunotaProcessando = null;
        this.gerandoNota = false;
    }

    /** Depois de gerar (ou desistir), fecha o modal. Se a nota terminou completa, recarrega a comparação. */
    fecharResultadoNota(): void {
        this.mostrarModalNota = false;
        const terminouCompleta = !!this.notaResultado;
        this.notaResultado = null;
        this.notaErro = '';
        this.notaItensNaoImportados = [];
        this.itensPreviewNota = [];
        this.notaAnexosResultado = null;
        this._fecharPreviewPdfInterno();
        if (terminouCompleta && this.jaBuscou) this.buscar(); // recarrega pra refletir os saldos corrigidos
    }

    /**
     * Colunas de texto livre (descrição/local) crescem pra caber o conteúdo e, sem
     * limite, empurram o grupo Sankhya inteiro pra fora da área visível (precisa
     * rolar pra o lado pra perceber que ele existe). Aqui a gente limita a largura
     * dessas colunas específicas (com reticências + title pro texto completo) pra
     * sobrar espaço pros dois grupos aparecerem juntos na tela.
     */
    classeCampo(campo: string): string {
        const c = (campo || '').toUpperCase();
        if (c.includes('DESCRICAO') || c.includes('DESCRPROD') || c.includes('DESCTECNICA') || c === 'LABEL') {
            return 'col-descricao';
        }
        if (c.includes('LOCAL')) {
            return 'col-local';
        }
        if (c === 'APE_PN' || c === 'REFFORN' || c === 'REFERENCIA' || c === 'REF') {
            return 'col-pn';
        }
        return '';
    }
}
